import dayjs from "dayjs";
import toast from "react-hot-toast";
import { apExistsInPriorPoasAdjCaps } from "../services/calculations/EntityCalculations";
import {
  getGroupData,
  getPoaIndex,
  getSelectedPeriod,
} from "../services/GroupContext";
import { cap_adjusted_values } from "./CapCurrencyValues";
import { generateCAPId, generateCAPIdByCompName } from "./capProcessor";
import {
  getDefaultCompanyLeaveDate,
  getOriginatingPoaMinStartDateJS,
  processDateJS,
} from "./dateProcessor";
import { getPriorPoas } from "./PeriodUtils";

const nonTradingKeys = [
  {
    cfd: "cfd_non_trading_loan_relationships_gbp",
    cfdadj: "cfdadj_non_trading_loan_relationships_gbp",
    bfd: "bfd_non_trading_loan_relationships_gbp",
    bfdadj: "bfdadj_non_trading_loan_relationships_gbp",
    cpr: "cpr_non_trading_loan_relationships_gbp",
    cpd: "cpd_non_trading_loan_relationships_gbp",
  },
  {
    cfd: "cfd_non_trading_derivatives_gbp",
    cfdadj: "cfdadj_non_trading_derivatives_gbp",
    bfd: "bfd_non_trading_derivatives_gbp",
    bfdadj: "bfdadj_non_trading_derivatives_gbp",
    cpr: "cpr_non_trading_derivatives_gbp",
    cpd: "cpd_non_trading_derivatives_gbp",
  },
];

const tradingKeys = [
  {
    cfd: "cfd_trading_loan_relationships_gbp",
    cfdadj: "cfdadj_trading_loan_relationships_gbp",
    bfd: "bfd_trading_loan_relationships_gbp",
    bfdadj: "bfdadj_trading_loan_relationships_gbp",
    cpr: "cpr_trading_loan_relationships_gbp",
    cpd: "cpd_trading_loan_relationships_gbp",
  },
  {
    cfd: "cfd_trading_derivatives_gbp",
    cfdadj: "cfdadj_trading_derivatives_gbp",
    bfd: "bfd_trading_derivatives_gbp",
    bfdadj: "bfdadj_trading_derivatives_gbp",
    cpr: "cpr_trading_derivatives_gbp",
    cpd: "cpd_trading_derivatives_gbp",
  },
];

const ofaKeys = [
  {
    cfd: "cfd_other_financing_gbp",
    cfdadj: "cfdadj_other_financing_gbp",
    bfd: "bfd_other_financing_gbp",
    bfdadj: "bfdadj_other_financing_gbp",
    cpr: "cpr_other_financing_gbp",
    cpd: "cpd_other_financing_gbp",
  },
];

const allKeys = [...nonTradingKeys, ...tradingKeys, ...ofaKeys];

const subtotalKeysGBP = {
  cfd: "cfd_subtotal_gbp",
  cfdadj: "cfdadj_subtotal_gbp",
  bfd: "bfd_subtotal_gbp",
  bfdadj: "bfdadj_subtotal_gbp",
  cpr: "cpr_subtotal_gbp",
  cpd: "cpd_total_gbp",
};

const getLcKeys = (gbpObject) => {
  const lcKeys = {};
  Object.keys(gbpObject).forEach((key) => {
    lcKeys[key] = gbpObject[key].replace("_gbp", "");
  });
  return lcKeys;
};

const tradingNonTradingConditionForEditBFD = (adjustedCap) => ({
  trading: !!adjustedCap?.trade_uncommercial_or_non_statutory,
  nonTrading: false,
});

const tradingNonTradingConditionForEditCFD = (adjustedCap) => ({
  trading: !!adjustedCap?.trade_small_or_negligible,
  nonTrading: !!adjustedCap?.investment_business_ceased,
});

const setBFDADJasNegativeBFD = (
  adjustedCap,
  conditionsMet,
  localCurrency = false,
) => {
  if (conditionsMet.trading) {
    tradingKeys.forEach((gbpKeys) => {
      const { adjKey, bfdKey } = localCurrency ? getLcKeys(gbpKeys) : gbpKeys;
      adjustedCap[adjKey] = Number(0 - adjustedCap[bfdKey]) || 0;
    });
  }

  if (conditionsMet.nonTrading) {
    nonTradingKeys.forEach((gbpKeys) => {
      const { adjKey, bfdKey } = localCurrency ? getLcKeys(gbpKeys) : gbpKeys;
      adjustedCap[adjKey] = Number(0 - adjustedCap[bfdKey]) || 0;
    });
  }

  if (Object.values(conditionsMet).includes(true)) {
    ofaKeys.forEach((gbpKeys) => {
      const { adjKey, bfdKey } = localCurrency ? getLcKeys(gbpKeys) : gbpKeys;
      adjustedCap[adjKey] = Number(0 - adjustedCap[bfdKey]) || 0;
    });

    recalculateTotal(adjustedCap, "bfdadj");
  }
};

const convertLCToGBP = (adjustedCap, gbpKey) => {
  const lcKey = gbpKey.replace("_gbp", "");
  adjustedCap[gbpKey] = Math.round(
    (Number(adjustedCap[lcKey]) || 0) / (Number(adjustedCap.fx_rate) || 1),
  );
};

export const recalculateAllTotals = (adjustedCap, localCurrency = false) => {
  Object.keys(subtotalKeysGBP).forEach((key) => {
    recalculateTotal(adjustedCap, key, localCurrency);
  });
};

const initiateAdjustedCapProps = (grpData, poa, compAP) => {
  const company = grpData?.companies?.find(
    (c) => c.company_ID === compAP?.company_ID,
  );

  const companyJoinDate = processDateJS(
    company?.date_join || getOriginatingPoaMinStartDateJS(),
  );
  const companyLeaveDate = processDateJS(
    company?.date_left || getDefaultCompanyLeaveDate(),
  );

  const group_period_start = processDateJS(poa?.period_start);
  const group_period_end = processDateJS(poa?.period_end);

  const relevantPeriodStart = processDateJS(
    dayjs.max(companyJoinDate, group_period_start),
  );
  const relevantPeriodEnd = processDateJS(
    dayjs.min(companyLeaveDate, group_period_end),
  );

  const start_date = processDateJS(compAP?.start_date);
  const end_date = processDateJS(compAP?.end_date);

  const inclusion_period_start = dayjs.max(
    processDateJS(relevantPeriodStart),
    start_date,
  );
  const inclusion_period_end = dayjs.min(
    processDateJS(relevantPeriodEnd),
    end_date,
  );

  const grpPeriodLength = group_period_end?.diff(group_period_start, "day") + 1;
  const relPeriodLength =
    relevantPeriodEnd?.diff(relevantPeriodStart, "day") + 1;
  const proportion_of_poa_as_group_company = relPeriodLength / grpPeriodLength;

  const inclusion_period_length = Math.max(
    inclusion_period_end?.diff(inclusion_period_start, "day") + 1,
    0,
  );

  const ap_length = end_date?.diff(start_date, "day") + 1;

  let adjCapPosInPoA = null;
  if (
    processDateJS(inclusion_period_start).isSame(
      processDateJS(companyJoinDate),
    ) ||
    processDateJS(inclusion_period_start).isSame(
      processDateJS(group_period_start),
    )
  ) {
    if (
      processDateJS(inclusion_period_end).isSame(
        processDateJS(companyLeaveDate),
      ) ||
      processDateJS(inclusion_period_end).isSame(
        processDateJS(group_period_end),
      )
    ) {
      adjCapPosInPoA = "StartEnd";
    } else {
      adjCapPosInPoA = "Start";
    }
  } else {
    if (
      processDateJS(inclusion_period_end).isSame(companyLeaveDate) ||
      processDateJS(inclusion_period_end).isSame(
        processDateJS(group_period_end),
      )
    ) {
      adjCapPosInPoA = "End";
    } else {
      adjCapPosInPoA = "Middle";
    }
  }

  let AdjCapPosinCompanyAccountingPeriod = null;
  if (
    processDateJS(inclusion_period_start).isSame(processDateJS(start_date)) &&
    processDateJS(inclusion_period_end).isSame(processDateJS(end_date))
  ) {
    AdjCapPosinCompanyAccountingPeriod = "StartEnd";
  } else if (
    processDateJS(inclusion_period_start).isSame(processDateJS(start_date))
  ) {
    AdjCapPosinCompanyAccountingPeriod = "Start";
  } else if (
    processDateJS(inclusion_period_end).isSame(processDateJS(end_date))
  ) {
    AdjCapPosinCompanyAccountingPeriod = "End";
  } else {
    AdjCapPosinCompanyAccountingPeriod = "Middle";
  }

  return {
    company_ID: company?.company_ID,
    company_name: company?.company_name,
    group_period_start: group_period_start?.toISOString() ?? null,
    group_period_end: group_period_end?.toISOString() ?? null,
    inclusion_period_start: inclusion_period_start?.toISOString() ?? null,
    inclusion_period_end: inclusion_period_end?.toISOString() ?? null,
    start_date: compAP?.start_date,
    end_date: compAP?.end_date,
    proportion_of_poa_as_group_company,
    inclusion_period_length,
    ap_length,
    inclusion_factor: inclusion_period_length / ap_length,
    AdjCapPosinCompanyAccountingPeriod,
    adjCapPosInPoA,
  };
};

export function recalculateTotal(adjustedCap, property, localCurrency = false) {
  const subtotalKey = localCurrency
    ? subtotalKeysGBP[property].replace("_gbp", "")
    : subtotalKeysGBP[property];
  adjustedCap[subtotalKey] = 0;

  allKeys.forEach((keys) => {
    const propKey = localCurrency
      ? keys[property].replace("_gbp", "")
      : keys[property];
    adjustedCap[subtotalKey] += Number(adjustedCap[propKey]) || 0;
  });
}

const calculateCfdAdjHelper = (keys, adjustedCap) => {
  const bfd = Number(adjustedCap[keys.bfd]) || 0;
  const bfdadj = Number(adjustedCap[keys.bfdadj]) || 0;
  const cpd = Number(adjustedCap[keys.cpd]) || 0;
  const cpr = Number(adjustedCap[keys.cpr]) || 0;

  return Number(-(bfd + bfdadj + cpd - cpr));
};

export function calculateCFDADJ(adjustedCap) {
  if (!adjustedCap) return;

  const conditionsMet = tradingNonTradingConditionForEditCFD(adjustedCap);

  if (conditionsMet.trading) {
    tradingKeys.forEach((gbpKeys) => {
      // GBP Calculatins
      adjustedCap[gbpKeys.cfdadj] = calculateCfdAdjHelper(gbpKeys, adjustedCap);

      // LC Calculations
      const lcKeys = getLcKeys(gbpKeys);
      adjustedCap[lcKeys.cfdadj] = calculateCfdAdjHelper(lcKeys, adjustedCap);
    });
  }

  if (conditionsMet.nonTrading) {
    nonTradingKeys.forEach((gbpKeys) => {
      // GBP Calculations
      adjustedCap[gbpKeys.cfdadj] = calculateCfdAdjHelper(gbpKeys, adjustedCap);

      // LC Calculations
      const lcKeys = getLcKeys(gbpKeys);
      adjustedCap[lcKeys.cfdadj] = calculateCfdAdjHelper(lcKeys, adjustedCap);
    });
  }

  if (Object.values(conditionsMet).includes(true)) {
    ofaKeys.forEach((gbpKeys) => {
      // GBP Calculations
      adjustedCap[gbpKeys.cfdadj] = calculateCfdAdjHelper(gbpKeys, adjustedCap);

      // LC Calculations
      const lcKeys = getLcKeys(gbpKeys);
      adjustedCap[lcKeys.cfdadj] = calculateCfdAdjHelper(lcKeys, adjustedCap);
    });

    recalculateTotal(adjustedCap, "cfdadj", true);
    recalculateTotal(adjustedCap, "cfdadj");
  }
}

export function calculateCFD(adjustedCap) {
  if (!adjustedCap) return;

  allKeys.forEach((gbpKeys) => {
    // GBP Calculations
    let bfd = Number(adjustedCap[gbpKeys.bfd]) || 0;
    let bfdadj = Number(adjustedCap[gbpKeys.bfdadj]) || 0;
    let cpd = Number(adjustedCap[gbpKeys.cpd]) || 0;
    let cpr = Number(adjustedCap[gbpKeys.cpr]) || 0;
    let cfdadj = Number(adjustedCap[gbpKeys.cfdadj]) || 0;

    adjustedCap[gbpKeys.cfd] = Number(bfd + bfdadj + cpd - cpr + cfdadj);

    // LC Calculations
    const lcKeys = getLcKeys(gbpKeys);
    bfd = Number(adjustedCap[lcKeys.bfd]) || 0;
    bfdadj = Number(adjustedCap[lcKeys.bfdadj]) || 0;
    cpd = Number(adjustedCap[lcKeys.cpd]) || 0;
    cpr = Number(adjustedCap[lcKeys.cpr]) || 0;
    cfdadj = Number(adjustedCap[lcKeys.cfdadj]) || 0;

    adjustedCap[lcKeys.cfd] = Number(bfd + bfdadj + cpd - cpr + cfdadj);
  });

  recalculateTotal(adjustedCap, "cfd", true);
  recalculateTotal(adjustedCap, "cfd");
}

export function setBFDasPrevCFD(adjustedCap, priorAdjustedCap) {
  allKeys.forEach((gbpKeys) => {
    // GBP Calculations
    adjustedCap[gbpKeys.bfd] = Number(priorAdjustedCap[gbpKeys.cfd]) || 0;

    adjustedCap[subtotalKeysGBP.bfd] += adjustedCap[gbpKeys.bfd];

    // LC Calculations
    const lcKeys = getLcKeys(gbpKeys);
    adjustedCap[lcKeys.bfd] = Number(priorAdjustedCap[lcKeys.cfd]) || 0;

    const subtotalKeysLC = getLcKeys(subtotalKeysGBP);
    adjustedCap[subtotalKeysLC.bfd] += adjustedCap[lcKeys.bfd];
  });

  recalculateTotal(adjustedCap, "bfd", true);
  recalculateTotal(adjustedCap, "bfd");
}

export function setBFDToZero(adjustedCap) {
  allKeys.forEach((gbpKeys) => {
    // GBP Calculation
    adjustedCap[gbpKeys.bfd] = 0;

    // LC Calculation
    const lcKeys = getLcKeys(gbpKeys);
    adjustedCap[lcKeys.bfd] = 0;
  });

  recalculateTotal(adjustedCap, "bfd", true);
  recalculateTotal(adjustedCap, "bfd");
}

export function setBFDADJForEditBFD(adjustedCap) {
  const conditionsMet = tradingNonTradingConditionForEditBFD(adjustedCap);

  setBFDADJasNegativeBFD(adjustedCap, conditionsMet, true);
}

export function setBFDADJForEditCFD(adjustedCap) {
  const conditionsMet = tradingNonTradingConditionForEditCFD(adjustedCap);

  setBFDADJasNegativeBFD(adjustedCap, conditionsMet, false);
  setBFDADJasNegativeBFD(adjustedCap, conditionsMet, true);
}

export function convertGBPToLC(adjustedCap) {
  allKeys.forEach((keys) => {
    Object.values(keys).forEach((gbpKey) => {
      const lcKey = gbpKey.replace("_gbp", "");
      adjustedCap[lcKey] = Math.round(
        (Number(adjustedCap[gbpKey]) || 0) * (Number(adjustedCap.fx_rate) || 1),
      );
    });
  });

  recalculateAllTotals(adjustedCap, true);
}

export function convertBfdLcValuesToGBP(adjustedCap) {
  allKeys.forEach((keys) => {
    convertLCToGBP(adjustedCap, keys.bfd);
    convertLCToGBP(adjustedCap, keys.bfdadj);
  });
}

export function convertAllLcValuesToGBP(adjustedCaps) {
  adjustedCaps.forEach((adjustedCap) => {
    allKeys.forEach((keys) => {
      Object.values(keys).forEach((gbpKey) => {
        convertLCToGBP(adjustedCap, gbpKey);
      });
    });

    recalculateAllTotals(adjustedCap);
  });
}

export function updateCaps(newCaps, oldCaps) {
  if (!newCaps || !oldCaps) {
    return oldCaps || newCaps;
  }

  const oldCapsMap = new Map(oldCaps.map((cap) => [generateCAPId(cap), cap]));

  return newCaps.map((newCap) => {
    const oldCap = oldCapsMap.get(generateCAPId(newCap));
    return { ...oldCap, ...newCap };
  });
}

export function updateCapsByName(newCaps, oldCaps) {
  if (!newCaps || !oldCaps) {
    return oldCaps || newCaps;
  }

  const oldCapsMap = new Map(
    oldCaps.map((cap) => [generateCAPIdByCompName(cap), cap]),
  );

  return newCaps.map((newCap) => {
    const oldCap = oldCapsMap.get(generateCAPIdByCompName(newCap));
    return { ...oldCap, ...newCap };
  });
}

export function carryOverAdjustedCaps(newCaps, oldCaps) {
  const oldCapsMap = new Map(
    oldCaps?.map((cap) => [generateCAPId(cap), cap]) ?? [],
  );
  const savedData = getGroupData();
  const poa = savedData.periods_of_account[getPoaIndex()];

  return newCaps?.map((newCap) => {
    const oldCap = oldCapsMap.get(generateCAPId(newCap));
    const calculatedCap = oldCap ?? newCap;
    return {
      ...calculatedCap,
      ...initiateAdjustedCapProps(savedData, poa, calculatedCap),
    };
  });
}

// Mostly copied from initiateAdjustedCapProps above
export function calculateCapProps(grpData, poa, compAP, companyMap) {
  const company = companyMap.get(compAP?.company_ID);

  const companyJoinDate = processDateJS(
    company?.date_join || getOriginatingPoaMinStartDateJS(),
  );
  const companyLeaveDate = processDateJS(
    company?.date_left || getDefaultCompanyLeaveDate(),
  );

  const group_period_start = processDateJS(poa?.period_start);
  const group_period_end = processDateJS(poa?.period_end);

  const relevantPeriodStart = processDateJS(
    dayjs.max(companyJoinDate, group_period_start),
  );
  const relevantPeriodEnd = processDateJS(
    dayjs.min(companyLeaveDate, group_period_end),
  );

  const start_date = processDateJS(compAP?.start_date);
  const end_date = processDateJS(compAP?.end_date);

  const inclusion_period_start = dayjs.max(
    processDateJS(relevantPeriodStart),
    start_date,
  );
  const inclusion_period_end = dayjs.min(
    processDateJS(relevantPeriodEnd),
    end_date,
  );

  const ap_length = end_date?.diff(start_date, "day") + 1;

  const start_outside_poa = processDateJS(compAP?.start_date)?.isBefore(
    processDateJS(inclusion_period_start),
  );
  const end_outside_poa = processDateJS(compAP?.end_date)?.isAfter(
    processDateJS(inclusion_period_end),
  );

  let pre_app_inclusion_factor;
  if (!apExistsInPriorPoasAdjCaps(grpData, compAP)) {
    const pre_app_start_date = start_date;
    const pre_app_end_date = inclusion_period_start.subtract(1, "day");

    const pre_app_length =
      pre_app_end_date.diff(pre_app_start_date, "days") + 1;
    pre_app_inclusion_factor = Math.min(pre_app_length / ap_length, 1);
  }

  return {
    ...compAP,
    start_outside_poa,
    end_outside_poa,
    ...(pre_app_inclusion_factor !== undefined && {
      pre_app_inclusion_factor,
    }),
  };
}

export function applyInclusionFactorAdjustments(
  adjCap,
  correspondingCompanyAP,
) {
  if (adjCap?.inclusion_factor && correspondingCompanyAP) {
    Object.entries(adjCap)?.forEach(([key, entry]) => {
      if (cap_adjusted_values?.includes(key)) {
        if (
          adjCap?.hasOwnProperty(key) &&
          adjCap[key] &&
          adjCap[key]?.manual_adjustment === true
        ) {
          adjCap.adjustment_status = "Manual Adjustment";
        } else {
          const compAPVal =
            correspondingCompanyAP &&
            Object.keys(correspondingCompanyAP).includes(key)
              ? correspondingCompanyAP[key]
              : 0;
          adjCap[key] = {
            value: (Number(compAPVal) || 0) * adjCap.inclusion_factor,
            manual_adjustment: false,
          };
        }
      } else {
        adjCap[key] = entry;
      }
    });
  }

  return adjCap;
}

export function getCompanyAPsforPoA(poa, savedData) {
  const saved_data = savedData ?? getGroupData();
  const compAPs = saved_data?.company_accounting_periods;
  const ukCompanyIDs = getSelectedPeriod()?.ukCompanyIDs;

  const { period_start, period_end } = poa;
  const poaStart = processDateJS(period_start);
  const poaEnd = processDateJS(period_end);

  const companyMap = new Map();
  saved_data?.companies?.forEach((comp) => {
    companyMap.set(comp.company_ID, {
      ...comp,
      dateJoinParsed: processDateJS(comp.date_join),
      dateLeaveParsed: processDateJS(comp.date_left),
    });
  });

  return compAPs
    ?.filter((cap) => {
      const capStartParsed = processDateJS(cap?.start_date);
      const capEndParsed = processDateJS(cap?.end_date);
      const capDateLeftParsed = processDateJS(cap?.date_left);

      return (
        (capDateLeftParsed?.isAfter(poaStart) ?? true) &&
        (capEndParsed?.isAfter(poaStart) ||
          capEndParsed?.isSame(poaStart, "day")) &&
        (processDateJS(cap?.date_join)?.isBefore(poaEnd) ?? true) &&
        (capStartParsed?.isBefore(poaEnd) ||
          capStartParsed?.isSame(poaEnd, "day"))
      );
    })
    .filter((cap) => {
      const apCompany = companyMap.get(cap.company_ID);
      if (!apCompany) return false;

      const isJoinNotAfterPoAEnd = !apCompany.dateJoinParsed?.isAfter(poaEnd);
      const isLeaveNotBeforePoAStart =
        !apCompany.dateLeaveParsed?.isBefore(poaStart);

      return (
        ukCompanyIDs?.includes(cap.company_ID) &&
        isJoinNotAfterPoAEnd &&
        isLeaveNotBeforePoAStart
      );
    })
    .sort((firstAP, secondAP) =>
      processDateJS(firstAP?.start_date).diff(
        processDateJS(secondAP?.start_date),
        "day",
      ),
    );
}

export function isDifferentPriorApCurrency(ap) {
  return (
    ap.priorApCurrency &&
    ap.currency &&
    String(ap.priorApCurrency).toLowerCase() !==
      String(ap.currency).toLowerCase()
  );
}

export function sortCaps(caps) {
  return caps?.sort((a, b) => {
    const nameComparison = a?.company_name?.localeCompare(b?.company_name);
    return nameComparison === 0
      ? processDateJS(a.start_date) - processDateJS(b.start_date)
      : nameComparison;
  });
}

export function getEntityInputCaps() {
  const groupData = getGroupData();
  const company_aps = getCompanyAPsforPoA(getSelectedPeriod());

  const companyMap = new Map(
    groupData?.companies?.map((c) => [c?.company_ID, c]) ?? [],
  );

  const caps = company_aps?.map((cap) => {
    delete cap["unmapped_fields"];

    const company = companyMap.get(cap?.company_ID);
    if (!company) {
      toast.error(`Failed to find company with ID: ${cap?.company_ID}`);
      return {};
    }

    const company_name = company.company_name;

    return {
      ...cap,
      company_ID: cap?.company_ID,
      company_name: company_name,
      currency: cap?.currency || "GBP",
      fx_rate: cap?.fx_rate || 1,
      from_onesource: cap?.from_onesource || company?.from_onesource || false,
      onesource_last_refresh_date_time: cap?.onesource_last_refresh_date_time,
    };
  });

  return sortCaps(caps);
}

export function getAdjCapsInclusionMetrics(
  company,
  cap,
  savedData = null,
  poaIndex = null,
) {
  const groupData = savedData ?? getGroupData();
  const pIndex = poaIndex ?? getPoaIndex();

  // Calculate inclusion period
  const company_join = processDateJS(
    company?.date_join || getOriginatingPoaMinStartDateJS(),
  );
  const company_leave = processDateJS(company?.date_left || "3030-01-01");
  const group_period_start = processDateJS(
    groupData?.periods_of_account[pIndex]?.period_start,
  );
  const group_period_end = processDateJS(
    groupData?.periods_of_account[pIndex]?.period_end,
  );

  if (
    company_leave?.isBefore(group_period_start) ||
    company_join?.isAfter(group_period_end)
  ) {
    // If adjusted cap is not included in PoA, return null
    return null;
  }

  const relevant_period_start = processDateJS(
    dayjs.max(company_join, group_period_start),
  );
  const relevant_period_end = processDateJS(
    dayjs.min(company_leave, group_period_end),
  );

  const apply_mean_accounting_date_election =
    cap?.apply_mean_accounting_date_election;
  const startDate = processDateJS(
    apply_mean_accounting_date_election
      ? cap?.actual_start_date
      : cap?.start_date,
  );
  const endDate = processDateJS(
    apply_mean_accounting_date_election ? cap?.actual_end_date : cap?.end_date,
  );

  const inclusion_period_start = processDateJS(
    dayjs.max(relevant_period_start, startDate),
  );
  const inclusion_period_end = processDateJS(
    dayjs.min(relevant_period_end, endDate),
  );

  const inclusion_period_length =
    inclusion_period_end?.diff(inclusion_period_start, "day") + 1;
  const ap_length = endDate?.diff(startDate, "day") + 1;
  const inclusion_factor = inclusion_period_length / ap_length;

  return {
    inclusion_period_start,
    inclusion_period_end,
    inclusion_period_length,
    ap_length,
    inclusion_factor,
  };
}

export function getIncludedAdjCaps(caps) {
  return caps?.filter((cap) => {
    const inclusion_factor = cap?.inclusion_factor;
    return inclusion_factor === null || inclusion_factor > 0;
  });
}

export function initAdjustedCaps(saved_data) {
  const selectedPeriod = getSelectedPeriod();
  const oldAdjustedCaps = selectedPeriod?.adjusted_caps;
  const newAdjustedCaps = getCompanyAPsforPoA(selectedPeriod, saved_data);
  return sortCaps(carryOverAdjustedCaps(newAdjustedCaps, oldAdjustedCaps));
}

export const getAmountEditability = (original_cap, company_period) => {
  const canEditPreAppAmount =
    company_period?.isFirstApForCompanyInGroup &&
    original_cap?.start_outside_poa;
  const canEditCurrentAmount = original_cap?.end_outside_poa;
  const canEditSomething = canEditPreAppAmount || canEditCurrentAmount;

  return {
    canEditPreAppAmount,
    canEditCurrentAmount,
    canEditSomething,
  };
};

/**
 * Generates a unique key for an adjusted cap entry (company + start date + end date)
 * @param {Object} cap - The cap object containing company_ID, start_date, and end_date
 * @returns {string} A pipe-separated key for map lookups
 */
export const getAdjustedCapKey = (cap) => {
  const startDate = processDateJS(cap?.start_date)?.format("YYYY-MM-DD");
  const endDate = processDateJS(cap?.end_date)?.format("YYYY-MM-DD");
  return `${cap?.company_ID}|${startDate}|${endDate}`;
};

/**
 * Generates a unique key for a company accounting period (company + start date)
 * @param {Object} cap - The cap object containing company_ID and start_date
 * @returns {string} A pipe-separated key for map lookups
 */
export const getCompanyAccountingPeriodKey = (cap) => {
  const startDate = processDateJS(cap?.start_date)?.format("YYYY-MM-DD");
  return `${cap?.company_ID}|${startDate}`;
};

// calculate pre_app, prior, current and future CIR values
export const getAdjustedRow = (
  original_cap,
  company_period,
  selectedPeriod,
  row,
  {
    newPreApp,
    newCurrent,
    priorPoas = null,
    editability = null,
    capHasPriorPreApp = null,
    priorMetricSums = null,
  } = {},
) => {
  const db_value = company_period[row?.metric_id];

  const prior_poas = priorPoas ?? getPriorPoas(company_period, selectedPeriod);

  const cap_has_prior_pre_app =
    capHasPriorPreApp ??
    prior_poas.some(
      (p_poa) => getAmountEditability(original_cap, p_poa)?.canEditPreAppAmount,
    );

  const { canEditPreAppAmount, canEditCurrentAmount } =
    editability ?? getAmountEditability(original_cap, company_period);

  const ap_total = row.accounting_period_total || 0;
  let [
    pre_app_cir_amount,
    prior_cir_amount,
    current_cir_amount,
    future_cir_amount,
  ] = [0, 0, 0, 0];

  // Pre App
  const pre_app_default_allocation = original_cap.pre_app_inclusion_factor
    ? ap_total * original_cap.pre_app_inclusion_factor
    : 0;
  const pre_app_stored_value =
    original_cap.pre_app_amounts?.[row.metric_id]?.value;
  const pre_app_current_value =
    row?.pre_app_cir_amount?.value ?? row?.pre_app_cir_amount;
  if (cap_has_prior_pre_app) {
    pre_app_cir_amount = pre_app_stored_value;
  } else if (canEditPreAppAmount && row.manual_adjustment) {
    pre_app_cir_amount =
      newPreApp ??
      pre_app_current_value ??
      pre_app_stored_value ??
      pre_app_default_allocation;
  } else {
    pre_app_cir_amount = pre_app_default_allocation;
  }

  // Prior
  if (priorMetricSums) {
    prior_cir_amount = priorMetricSums[row?.metric_id] || 0;
  } else {
    prior_cir_amount = original_cap.start_outside_poa
      ? prior_poas?.reduce((acc, matching_cap) => {
          const metric = matching_cap?.[row?.metric_id];
          return acc + (metric?.value ?? metric ?? 0);
        }, 0)
      : 0;
  }

  // Current
  const current_default_allocation = ap_total * company_period.inclusion_factor;
  const current_stored_value = db_value?.value ?? db_value;
  const current_current_value =
    row?.current_cir_amount?.value ?? row?.current_cir_amount;
  if (canEditCurrentAmount) {
    if (row.manual_adjustment) {
      current_cir_amount =
        newCurrent ??
        current_current_value ??
        current_stored_value ??
        current_default_allocation;
    } else {
      current_cir_amount = current_default_allocation;
    }
  } else {
    current_cir_amount = ap_total - pre_app_cir_amount - prior_cir_amount;
  }

  // Future
  future_cir_amount =
    ap_total - pre_app_cir_amount - prior_cir_amount - current_cir_amount;

  return {
    ...row,
    pre_app_cir_amount,
    prior_cir_amount,
    current_cir_amount,
    future_cir_amount,
  };
};

function isDateLike(v) {
  return (
    v instanceof Date ||
    dayjs?.isDayjs?.(v) ||
    (typeof v === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(v))
  );
}

function isValueEquivalent(a, b) {
  if (a === b) return true;

  // Handle null/undefined and 0 equality
  if ((a == null && b === 0) || (b == null && a === 0)) return true;

  if (a == null || b == null) return false;

  // Treat false and 0 as equal
  if ((a === false && b === 0) || (a === 0 && b === false)) {
    return true;
  }

  if (isDateLike(a) || isDateLike(b)) {
    const da = processDateJS(a);
    const db = processDateJS(b);
    if (da && db) return da.valueOf() === db.valueOf();
    return false;
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isValueEquivalent(a[i], b[i])) return false;
    }
    return true;
  }

  if (isPlainObject(a) || isPlainObject(b)) {
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();
    if (aKeys.length !== bKeys.length) return false;
    for (let i = 0; i < aKeys.length; i++) {
      const k = aKeys[i];
      if (k !== bKeys[i]) return false;
      if (!isValueEquivalent(a[k], b[k])) return false;
    }
    return true;
  }

  return false;
}

function isPlainObject(v) {
  return (
    v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    !(v instanceof Date) &&
    !dayjs?.isDayjs?.(v)
  );
}

function checkChangedMetrics(capA, capB) {
  const changed = {};
  if (!capA || !capB) return changed;

  for (const key of Object.keys(capB)) {
    const aVal = capA[key];
    const bVal = capB[key];
    if (!isValueEquivalent(aVal, bVal)) {
      changed[key] = { from: aVal, to: bVal };
    }
  }
  return changed;
}

export function checkChangedMetricsList(capsA, capsB) {
  const changes = [];
  const capsBById = new Map();

  capsB?.forEach((capB) => {
    capsBById.set(generateCAPId(capB), capB);
  });

  capsA?.forEach((capA) => {
    const capAId = generateCAPId(capA);
    const capB = capsBById.get(capAId);
    changes.push(checkChangedMetrics(capA, capB));
  });
  return changes;
}
